import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useTable } from '@refinedev/antd'
import type { HttpError } from '@refinedev/core'
import { Breadcrumb, Button, Space, Table } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import {
  BULK_TERMINATE_CONFIRM_TEXT,
  BULK_TERMINATE_DESCRIPTION,
  TerminateConfirmModal,
} from '@/components/common/TerminateConfirmModal'
import { STORAGE_TERMINAL_STATUSES } from '@/constants/statuses'
import {
  labelsForIds,
  useResourceTermination,
} from '@/hooks/useResourceTermination'
import { useTableRowNavigation } from '@/hooks/useTableRowNavigation'
import { UsersCacheProvider } from '@/hooks/useUsersByIds'
import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'
import { searchByNameOrId } from '@/utils/searchFilters'

import { storageColumns } from './columns'
import { CreateStorageModal } from './components/CreateStorageModal'
import { StorageFilterForm } from './components/StorageFilterForm'

type UserStorageDto = components['schemas']['UserStorageDto']

export default function StorageListPage() {
  const {
    selectedRowKeys,
    setSelectedRowKeys,
    terminateTarget,
    setTerminateTarget,
    doTerminate,
    bulkTerminateOpen,
    openBulkTerminate,
    closeBulkTerminate,
  } = useResourceTermination<UserStorageDto>({
    resourceLabel: 'storage',
    terminateFn: (ids) =>
      client.POST('/v1/admin/storages/terminate', {
        body: { storageIds: ids },
      }),
  })
  const onRow = useTableRowNavigation('storages')
  const [createOpen, setCreateOpen] = useState(false)

  const { tableProps, searchFormProps, filters } = useTable<
    UserStorageDto,
    HttpError,
    { search?: string; status?: string; userId?: string; clusterId?: string }
  >({
    resource: 'storages',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
    onSearch: (values) => [
      ...searchByNameOrId(values.search, 'ids', true),
      { field: 'status', operator: 'eq', value: values.status || undefined },
      { field: 'userId', operator: 'eq', value: values.userId || undefined },
      {
        field: 'clusterId',
        operator: 'eq',
        value: values.clusterId || undefined,
      },
    ],
  })

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'Storage' }]} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Space>
          {selectedRowKeys.length > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={openBulkTerminate}
            >
              Terminate ({selectedRowKeys.length})
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            Create Storage
          </Button>
        </Space>
      </div>

      <StorageFilterForm searchFormProps={searchFormProps} filters={filters} />

      <UsersCacheProvider
        ids={(tableProps.dataSource ?? []).map((row) => row.userId)}
      >
        <Table
          {...tableProps}
          pagination={tablePaginationConfig(tableProps)}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as string[]),
            getCheckboxProps: (record: UserStorageDto) => ({
              disabled: STORAGE_TERMINAL_STATUSES.has(record.status),
            }),
          }}
          onRow={onRow}
          columns={storageColumns(setTerminateTarget)}
          scroll={{ x: 700 }}
        />
      </UsersCacheProvider>

      <CreateStorageModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      <TerminateConfirmModal
        open={terminateTarget !== null}
        title="Terminate Storage"
        resourceName={terminateTarget?.name ?? ''}
        onConfirm={() => doTerminate([terminateTarget?.id ?? ''])}
        onCancel={() => setTerminateTarget(null)}
      />

      <TerminateConfirmModal
        open={bulkTerminateOpen}
        title={`Terminate ${selectedRowKeys.length} storage(s)`}
        targets={labelsForIds(selectedRowKeys, tableProps.dataSource)}
        confirmText={BULK_TERMINATE_CONFIRM_TEXT}
        description={BULK_TERMINATE_DESCRIPTION}
        onConfirm={() => doTerminate(selectedRowKeys)}
        onCancel={closeBulkTerminate}
      />
    </div>
  )
}
